import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowsLeftRight, FloppyDisk, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { logError } from "@/lib/log";

const EMPTY_CONFIG = {
  primary_account_id: null,
  collector_account_ids: [],
  source_to_collector: {},
  vip_account_ids: [],
  market_watcher_enabled: false,
};
const EMPTY_RESOURCE_SETTINGS = {
  enabled: false,
  daily_time: "02:00",
  allowed_fish_keys: [],
  listing_scope: "source",
  next_run_at: null,
};

const number = (value) => Number.parseInt(value || "0", 10) || 0;
const coins = (value) => new Intl.NumberFormat("id-ID").format(value || 0);
const tierLabel = (tier) => tier === "shiny" ? "Secret Shiny" :
  tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : "";

export default function CoinTransfer() {
  const [accounts, setAccounts] = useState([]);
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [snapshots, setSnapshots] = useState({});
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [execution, setExecution] = useState(null);
  const [market, setMarket] = useState({ listing_count: 0, insights: [] });
  const [learning, setLearning] = useState({ mode: "advisory", outcome_count: 0, recommendations: [] });
  const [marketScanning, setMarketScanning] = useState(false);
  const [marketSearch, setMarketSearch] = useState("");
  const [resourceListings, setResourceListings] = useState([]);
  const [resourceSettings, setResourceSettings] = useState(EMPTY_RESOURCE_SETTINGS);
  const [resourceLibrary, setResourceLibrary] = useState({ items: [] });
  const [resourceRun, setResourceRun] = useState({ status: "idle" });
  const [resourceNowRun, setResourceNowRun] = useState({ status: "idle" });
  const [startingResourceNow, setStartingResourceNow] = useState(false);
  const [resourceNowError, setResourceNowError] = useState("");
  const [librarySearch, setLibrarySearch] = useState("");
  const [refreshingLibrary, setRefreshingLibrary] = useState(false);
  const [savingResourceSettings, setSavingResourceSettings] = useState(false);
  const [collectionPlan, setCollectionPlan] = useState(null);
  const [collectionPlanning, setCollectionPlanning] = useState(false);
  const [collectionStarting, setCollectionStarting] = useState(false);
  const [closingExecution, setClosingExecution] = useState(false);
  const [accountsError, setAccountsError] = useState(false);
  const [configError, setConfigError] = useState(false);

  const labels = useMemo(
    () => Object.fromEntries(accounts.map((account) => [account.id, account.label])),
    [accounts]
  );
  const hasActiveResourceListings = resourceListings.some((item) => item.status === "listed");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get("/coin-transfer/accounts"),
        api.get("/coin-transfer/config"),
        api.get("/coin-transfer/market/insights"),
        api.get("/coin-transfer/market/learning"),
        api.get("/coin-transfer/market/resource/statuses"),
        api.get("/coin-transfer/resource-automation/settings"),
        api.get("/coin-transfer/resource-automation/library"),
        api.get("/coin-transfer/resource-automation/last-run"),
        api.get("/coin-transfer/resource-automation/now/last-run"),
        api.get("/coin-transfer/executions/latest"),
      ]);
      const data = (index) => results[index].status === "fulfilled" ? results[index].value.data : null;
      const accountData = data(0);
      const configData = data(1);
      if (accountData) {
        const list = accountData.accounts || [];
        setAccounts(list);
        setAccountsError(false);
        setSnapshots((current) => Object.fromEntries(list.map((account) => [
          account.id,
          {
            balance: account.profile?.balance ?? current[account.id]?.balance ?? 0,
            used_listings: account.profile?.used_listings ?? current[account.id]?.used_listings ?? 0,
            available_items: account.profile?.available_items ?? current[account.id]?.available_items ?? 0,
          },
        ])));
      } else {
        setAccountsError(true);
      }
      if (configData) {
        setConfig({ ...EMPTY_CONFIG, ...configData });
        setConfigError(false);
      } else {
        setConfigError(true);
      }
      if (data(2)) setMarket(data(2));
      if (data(3)) setLearning(data(3));
      if (data(4)) setResourceListings(data(4).runs || []);
      if (data(5)) setResourceSettings({ ...EMPTY_RESOURCE_SETTINGS, ...data(5) });
      if (data(6)) setResourceLibrary(data(6));
      if (data(7)) setResourceRun(data(7));
      if (data(8)) setResourceNowRun(data(8));
      if (data(9)) setExecution(data(9).run_id ? data(9) : null);
      results.forEach((result, index) => {
        if (result.status === "rejected" && !(index === 9 && result.reason?.response?.status === 404)) {
          logError(`Gagal memuat bagian Pindah Koin (${index}):`, result.reason);
        }
      });
      if (!accountData && !silent) toast.error("Daftar akun gagal dimuat. Coba lagi.");
    } catch (error) {
      logError("Gagal memuat pindah koin:", error);
      toast.error(error?.response?.data?.detail || "Data pindah koin gagal dimuat");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!["queued", "scanning", "running"].includes(resourceNowRun.status)) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const response = await api.get("/coin-transfer/resource-automation/now/last-run");
        setResourceNowRun(response.data || { status: "idle" });
        if (!["queued", "scanning", "running"].includes(response.data?.status)) load(true);
      } catch (error) {
        logError("Gagal membaca listing sekarang:", error);
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [resourceNowRun.status, load]);

  useEffect(() => {
    if (!hasActiveResourceListings) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const response = await api.get("/coin-transfer/market/resource/statuses");
        setResourceListings(response.data?.runs || []);
      } catch (error) {
        logError("Gagal membaca status listing resource:", error);
      }
    }, 30000);
    return () => window.clearInterval(timer);
  }, [hasActiveResourceListings]);

  useEffect(() => {
    if (!execution?.run_id || !["queued", "running"].includes(execution.status)) {
      return undefined;
    }
    const timer = window.setInterval(async () => {
      try {
        const response = await api.get(`/coin-transfer/executions/${execution.run_id}`);
        setExecution(response.data);
      } catch (error) {
        logError("Gagal membaca status batch:", error);
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [execution?.run_id, execution?.status]);

  const roleOf = (accountId) => {
    if (config.primary_account_id === accountId) return "primary";
    if (config.collector_account_ids.includes(accountId)) return "collector";
    if (Object.hasOwn(config.source_to_collector, accountId)) return "source";
    return "ignored";
  };

  const visibleLibrary = (resourceLibrary.items || []).filter((item) => (
    `${item.name} ${tierLabel(item.tier)}`.toLocaleLowerCase("id-ID").includes(librarySearch.toLocaleLowerCase("id-ID"))
  ));
  const visibleMarket = (market.insights || []).filter((item) => (
    `${item.name} ${tierLabel(item.tier)}`.toLocaleLowerCase("id-ID").includes(marketSearch.toLocaleLowerCase("id-ID"))
  ));

  const toggleLibraryFish = (fishKey, checked) => {
    setResourceSettings((current) => ({
      ...current,
      allowed_fish_keys: checked
        ? [...new Set([...(current.allowed_fish_keys || []), fishKey])]
        : (current.allowed_fish_keys || []).filter((key) => key !== fishKey),
    }));
  };

  const setRole = (accountId, role) => {
    const next = {
      ...config,
      collector_account_ids: config.collector_account_ids.filter((id) => id !== accountId),
      source_to_collector: Object.fromEntries(
        Object.entries(config.source_to_collector).filter(([source, collector]) => (
          source !== accountId && collector !== accountId
        ))
      ),
      primary_account_id: config.primary_account_id === accountId ? null : config.primary_account_id,
    };
    if (role === "primary") next.primary_account_id = accountId;
    if (role === "collector") next.collector_account_ids = [...next.collector_account_ids, accountId];
    if (role === "source") {
      const collector = next.collector_account_ids[0];
      if (!collector) {
        toast.error("Pilih collector terlebih dahulu");
        return;
      }
      next.source_to_collector = { ...next.source_to_collector, [accountId]: collector };
    }
    setConfig(next);
    setPlan(null);
    setCollectionPlan(null);
  };

  const setCollector = (sourceId, collectorId) => {
    setConfig((current) => ({
      ...current,
      source_to_collector: { ...current.source_to_collector, [sourceId]: collectorId },
    }));
    setPlan(null);
    setCollectionPlan(null);
  };

  const toggleVip = (accountId, checked) => {
    setConfig((current) => ({
      ...current,
      vip_account_ids: checked
        ? [...new Set([...current.vip_account_ids, accountId])]
        : current.vip_account_ids.filter((id) => id !== accountId),
    }));
    setPlan(null);
    setCollectionPlan(null);
  };

  const updateSnapshot = (accountId, key, value) => {
    setSnapshots((current) => ({
      ...current,
      [accountId]: { ...current[accountId], [key]: number(value) },
    }));
    setPlan(null);
  };

  const save = async ({ syncProfiles = true } = {}) => {
    if (configError) {
      toast.error("Pengaturan akun belum dimuat. Coba lagi.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...config };
      delete payload.user_id;
      delete payload.updated_at;
      delete payload.execution_enabled;
      await api.put("/coin-transfer/config", payload);
      if (syncProfiles) {
        const syncResponse = await api.post("/coin-transfer/profiles/sync");
        const queued = syncResponse.data.queued || 0;
        toast.success(
          queued
            ? `Pembagian disimpan. Mengambil profil ${queued} akun...`
            : "Pembagian disimpan. Tidak ada akun Telegram yang terhubung."
        );
        [2500, 7000, 14000, 25000, 40000].forEach((delay) => (
          window.setTimeout(() => load(true), delay)
        ));
      } else {
        toast.success("Pembagian akun disimpan");
      }
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Pembagian akun gagal disimpan");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const makePlan = async () => {
    setPlanning(true);
    try {
      if (!await save({ syncProfiles: false })) return;
      const response = await api.post("/coin-transfer/plan", {
        snapshots: accounts.map((account) => ({
          account_id: account.id,
          ...snapshots[account.id],
        })),
      });
      setPlan(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Simulasi gagal dibuat");
    } finally {
      setPlanning(false);
    }
  };

  const scanMarket = async () => {
    setMarketScanning(true);
    try {
      const response = await api.post("/coin-transfer/market/scan");
      setMarket(response.data);
      const advice = await api.get("/coin-transfer/market/learning");
      setLearning(advice.data);
      toast.success(`Market terbaca: ${response.data.listing_count || 0} penjualan`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Market gagal dibaca");
    } finally {
      setMarketScanning(false);
    }
  };

  const refreshLibrary = async () => {
    setRefreshingLibrary(true);
    try {
      const response = await api.post("/coin-transfer/resource-automation/library/refresh");
      setResourceLibrary(response.data);
      toast.success(`Library terbaca: ${response.data.items?.length || 0} jenis ikan`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Library ikan gagal dibaca");
    } finally {
      setRefreshingLibrary(false);
    }
  };

  const saveResourceSettings = async () => {
    setSavingResourceSettings(true);
    try {
      const response = await api.put("/coin-transfer/resource-automation/settings", {
        enabled: resourceSettings.enabled,
        daily_time: resourceSettings.daily_time,
        allowed_fish_keys: resourceSettings.allowed_fish_keys || [],
        listing_scope: resourceSettings.listing_scope || "source",
      });
      setResourceSettings(response.data);
      toast.success("Library dan jadwal listing tersimpan");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Jadwal listing gagal disimpan");
    } finally {
      setSavingResourceSettings(false);
    }
  };

  const startResourceNow = async () => {
    const count = resourceSettings.allowed_fish_keys?.length || 0;
    if (!count) { toast.error("Pilih minimal satu jenis ikan di library"); return; }
    const roles = { source: "resource", collector: "collector", primary: "utama", all: "semua akun" };
    if (!window.confirm(
      `Jalankan listing sekarang untuk ${roles[resourceSettings.listing_scope || "source"]}? ` +
      `${count} jenis ikan terpilih. Tiap akun akan memakai slot yang tersedia; harga dibaca ulang dari market.`
    )) return;
    setResourceNowError("");
    setStartingResourceNow(true);
    try {
      const response = await api.post("/coin-transfer/resource-automation/now", {
        allowed_fish_keys: resourceSettings.allowed_fish_keys || [],
        listing_scope: resourceSettings.listing_scope || "source",
        confirmation: "LISTING SEKARANG",
      });
      setResourceNowRun(response.data);
      toast.success("Listing sekarang dimulai. Status akan diperbarui otomatis.");
    } catch (error) {
      const message = error?.response?.data?.detail || "Listing sekarang gagal dimulai";
      setResourceNowError(message);
      toast.error(message);
    } finally {
      setStartingResourceNow(false);
    }
  };

  const previewCollection = async () => {
    setCollectionPlanning(true);
    try {
      const response = await api.post("/coin-transfer/collect/preview");
      setCollectionPlan(response.data);
      toast.success("Simulasi saldo live siap. Listing harian belum dijalankan.");
      window.setTimeout(() => document.getElementById("coin-collection")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Saldo live belum bisa dihitung");
    } finally {
      setCollectionPlanning(false);
    }
  };

  const startCollection = async () => {
    if (!collectionPlan?.plan_id) return;
    const approved = window.confirm(
      `Kumpulkan koin lewat ${collectionPlan.transactions?.length || 0} transaksi? ` +
      "Listing resource hari ini akan dilewati. Batch berhenti jika hasil transaksi tidak pasti."
    );
    if (!approved) return;
    setCollectionStarting(true);
    try {
      const response = await api.post("/coin-transfer/collect/execute", {
        plan_id: collectionPlan.plan_id,
        confirmation: "KUMPULKAN",
      });
      setExecution(response.data);
      setCollectionPlan(null);
      toast.success("Pengumpulan koin dimulai");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Pengumpulan koin gagal dimulai");
    } finally {
      setCollectionStarting(false);
    }
  };

  const closeExecution = async () => {
    if (!execution?.run_id || !window.confirm(
      "Periksa dulu listing dan saldo kedua akun di Fish It. Tutup batch ini tanpa " +
      "mengulang transaksi? Setelah itu buat batch live baru dari saldo terkini."
    )) return;
    setClosingExecution(true);
    try {
      await api.post(`/coin-transfer/executions/${execution.run_id}/close`, {
        confirmation: "TUTUP BATCH",
      });
      setCollectionPlan(null);
      await load(true);
      toast.success("Batch ditutup. Baca ulang saldo sebelum mengumpulkan koin lagi.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Batch gagal ditutup");
    } finally {
      setClosingExecution(false);
    }
  };

  if (loading) return <div className="text-slate-500">Memuat...</div>;

  return (
    <div className="space-y-5 md:space-y-6" data-testid="coin-transfer-page">
      <header>
        <div className="text-xs font-bold uppercase tracking-[0.16em] text-amber-300 mb-2">Pengelolaan</div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold flex items-center gap-3">
          <ArrowsLeftRight size={28} weight="duotone" className="text-amber-300 shrink-0" />
          Pindah Koin
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">Atur akun, listing ikan, dan pengumpulan koin dari satu halaman.</p>
      </header>

      <nav className="coin-section-nav flex gap-2 overflow-x-auto pb-1" aria-label="Bagian Pindah Koin">
        <a href="#coin-market">Market</a>
        <a href="#coin-library">Library ikan</a>
        <a href="#coin-accounts">Peran akun</a>
        <a href="#coin-collection">Kumpulkan koin</a>
        <a href="#coin-simulation">Simulasi</a>
      </nav>

      <section id="coin-market" className="scroll-mt-24">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-heading text-xl font-bold">Pembaca Market</h2>
            <p className="text-sm text-slate-500">
              Harga tengah dari ikan yang benar-benar terjual di kanal Fish It Market, khusus Secret, Secret Shiny, dan Celestial.
            </p>
          </div>
          <Button onClick={scanMarket} disabled={marketScanning} variant="outline" className="border-white/10">
            {marketScanning ? "Membaca..." : "Baca Market Sekarang"}
          </Button>
        </div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3 mb-4">
          <div>
            <div className="text-sm font-medium">Pantau otomatis setiap 15 menit</div>
            <div className="text-xs text-slate-500">
              {resourceSettings.enabled
                ? "Jadwal harian aktif; market dibaca saat jadwal listing."
                : "Hanya membaca market; tidak memasang atau membeli listing."}
            </div>
          </div>
          <Switch
            checked={Boolean(config.market_watcher_enabled)}
            onCheckedChange={(checked) => {
              setConfig((current) => ({ ...current, market_watcher_enabled: checked }));
              setPlan(null);
            }}
            aria-label="Pantau market otomatis"
          />
        </div>
        <div className="flex flex-wrap gap-5 text-xs text-slate-500 mb-3">
          <span>{market.listing_count || 0} penjualan relevan terbaca</span>
          {market.messages_scanned != null && <span>{market.messages_scanned} pesan kanal diperiksa</span>}
          {market.scanned_at && (
            <span>Diperbarui {new Date(market.scanned_at).toLocaleString("id-ID", { hour12: false })}</span>
          )}
        </div>
        <Input
          value={marketSearch}
          onChange={(event) => setMarketSearch(event.target.value)}
          placeholder="Cari ikan, misalnya Lumba-lumba Nimbus"
          aria-label="Cari harga market"
          className="mb-2 bg-[#05050A] border-white/10"
        />
        <div className="max-h-72 overflow-y-auto">
        {visibleMarket.map((item) => (
          <div key={item.key} className="grid grid-cols-2 sm:grid-cols-[minmax(130px,1fr)_90px_120px_110px] gap-x-3 gap-y-1 border-t border-white/[0.06] py-3 text-sm">
            <span className="col-span-2 sm:col-span-1 font-medium sm:truncate">{item.name}</span>
            <span className="text-slate-400">{tierLabel(item.tier)}</span>
            <span className="text-right sm:text-left font-semibold">{coins(item.recommended_price)}</span>
            <span className="col-span-2 sm:col-span-1 text-xs text-slate-400">{item.sold_count || 0} terjual</span>
          </div>
        ))}
        {visibleMarket.length === 0 && (
          <p className="text-sm text-slate-600 py-3">Belum ada data market.</p>
        )}
        </div>
        <p className="text-xs text-slate-600 mt-2">
          Hanya penjualan terkonfirmasi yang dihitung; ikan perlu minimal 5 penjualan sebelum dipakai untuk harga listing otomatis. Sumber: <a href="https://t.me/fish_it_market" target="_blank" rel="noopener noreferrer" className="underline">Fish It Market</a>.
        </p>
        <div className="mt-5 rounded-lg border border-sky-400/20 bg-sky-400/[0.04] p-4" data-testid="market-learning">
          <h3 className="font-heading text-lg font-semibold">AI Pembelajaran Harga</h3>
          <p className="text-xs text-slate-400 mt-1">
            Mode saran. AI membandingkan harga dekat median market dan belajar dari {learning.outcome_count || 0} hasil listing yang terpantau selama 24 jam.
            Listing yang hasilnya belum pasti tidak dihitung. Saran ini tidak mengubah harga listing otomatis.
          </p>
          <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-white/[0.06]">
            {(learning.recommendations || []).map((item) => (
              <div key={item.key} className="grid grid-cols-[minmax(130px,1fr)_100px_100px] gap-3 py-2 text-sm">
                <span className="truncate">{item.name} <span className="text-slate-500">({tierLabel(item.tier)})</span></span>
                <span>{coins(item.recommended_price)}</span>
                <span className="text-xs text-slate-500">
                  {item.mode === "learning" ? `${item.learning_outcomes} hasil dipelajari` : "Harga awal"}
                </span>
              </div>
            ))}
            {!learning.recommendations?.length && <p className="text-sm text-slate-500 py-2">Baca market dahulu untuk mendapatkan saran.</p>}
          </div>
        </div>
        {resourceListings.length > 0 && (
          <div className="mt-4 rounded-lg border border-yellow-400/20 bg-yellow-400/[0.04] p-3 text-sm">
            <strong>Listing akun resource</strong>
            <div className="mt-2 divide-y divide-white/[0.06]">
              {resourceListings.map((item) => (
                <div key={item.run_id} className="py-2 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-slate-300">
                      {labels[item.account_id] || "Resource"} · {item.fish_name} ({item.tier}) · {coins(item.price)}
                    </span>
                    <span className={item.status === "sold" ? "text-emerald-400" : "text-yellow-300"}>
                      {item.status === "listed" ? "Sedang dijual" : item.status}
                    </span>
                  </div>
                  {item.checked_at && (
                    <p className="text-xs text-slate-500 mt-1">
                      Terakhir diperiksa {new Date(item.checked_at).toLocaleString("id-ID", { hour12: false })}
                    </p>
                  )}
                  {item.error && <p className="text-xs text-red-300 mt-1">{item.error}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section id="coin-library" className="scroll-mt-24" data-testid="resource-library">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="font-heading text-xl font-bold">Library ikan semua akun</h2>
            <p className="text-sm text-slate-500">
              Membaca Tas akun resource, collector, dan utama. Hanya jenis yang Anda pilih boleh dijual oleh kelompok akun yang dipilih.
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Library hanya memuat Secret, Secret Shiny, dan Celestial.
            </p>
          </div>
          <Button onClick={refreshLibrary} disabled={refreshingLibrary} variant="outline" className="border-white/10">
            {refreshingLibrary ? "Membaca Tas..." : "Perbarui Library"}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
          <span>{resourceSettings.allowed_fish_keys?.length || 0} jenis dipilih</span>
          <Button
            type="button" variant="outline" className="border-white/10"
            onClick={() => setResourceSettings((current) => ({
              ...current,
              allowed_fish_keys: [...new Set([
                ...(current.allowed_fish_keys || []),
                ...(resourceLibrary.items || []).filter((item) => item.tier === "secret").map((item) => item.key),
              ])],
            }))}
          >
            Pilih semua Secret
          </Button>
          <Button
            type="button" variant="outline" className="border-white/10"
            onClick={() => setResourceSettings((current) => ({ ...current, allowed_fish_keys: [] }))}
          >
            Kosongkan
          </Button>
        </div>
        <Input
          value={librarySearch}
          onChange={(event) => setLibrarySearch(event.target.value)}
          placeholder="Cari nama atau tier ikan"
          aria-label="Cari ikan di library"
          className="mb-3 bg-[#05050A] border-white/10"
        />
        <div className="max-h-72 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/[0.06]">
          {visibleLibrary.map((item) => (
            <label key={item.key} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={(resourceSettings.allowed_fish_keys || []).includes(item.key)}
                onChange={(event) => toggleLibraryFish(item.key, event.target.checked)}
                aria-label={`Izinkan ${item.name} ${item.tier}`}
              />
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              <span className="text-slate-500">{tierLabel(item.tier)}</span>
              <span className="text-slate-500">{item.fish_count} ikan · {item.account_count} akun</span>
            </label>
          ))}
          {visibleLibrary.length === 0 && (
            <p className="p-3 text-sm text-slate-500">
              {resourceLibrary.items?.length ? "Ikan tidak ditemukan." : "Perbarui library untuk membaca Tas semua akun."}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-4">
          <div>
            <label htmlFor="resource-listing-scope" className="block text-xs text-slate-500 mb-1">Akun yang boleh menjual</label>
            <select
              id="resource-listing-scope" value={resourceSettings.listing_scope || "source"}
              onChange={(event) => setResourceSettings((current) => ({ ...current, listing_scope: event.target.value }))}
              className="h-10 rounded-md border border-white/10 bg-[#05050A] px-3 text-sm text-white"
            >
              <option value="source">Resource</option>
              <option value="collector">Collector</option>
              <option value="primary">Akun utama</option>
              <option value="all">Semua akun</option>
            </select>
          </div>
          <div>
            <label htmlFor="resource-daily-time" className="block text-xs text-slate-500 mb-1">Jam listing harian (WIB)</label>
            <Input
              id="resource-daily-time" type="time" value={resourceSettings.daily_time || "02:00"}
              onChange={(event) => setResourceSettings((current) => ({ ...current, daily_time: event.target.value }))}
              className="w-36 bg-[#05050A] border-white/10"
            />
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Switch
              checked={Boolean(resourceSettings.enabled)}
              onCheckedChange={(checked) => setResourceSettings((current) => ({ ...current, enabled: checked }))}
              aria-label="Aktifkan listing resource harian"
            />
            <span className="text-sm">Listing otomatis setiap hari</span>
          </div>
          <Button onClick={saveResourceSettings} disabled={savingResourceSettings} className="mt-4 bg-yellow-500 text-black hover:bg-yellow-400">
            {savingResourceSettings ? "Menyimpan..." : "Simpan Library & Jadwal"}
          </Button>
          <Button
            onClick={startResourceNow}
            disabled={startingResourceNow || ["queued", "scanning", "running"].includes(resourceNowRun.status)}
            className="mt-4 bg-pink-600 text-white hover:bg-pink-500"
          >
            {startingResourceNow ? "Memulai..." : "Jalankan Listing Sekarang"}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Tombol langsung mengisi jatah listing akun pilihan (sesuai batas dari Fish It), tanpa perlu diklik ulang. Akun berbeda berjalan bersamaan; listing berikutnya pada akun yang sama menunggu jeda minimal 20 detik. Akun Abaikan tidak diproses. Listing dari otomasi ini yang belum terjual dibatalkan lewat Mini App mulai 00.01 WIB. Jadwal harian tetap menjual maksimal satu ikan per akun. Pengumpulan koin tidak berjalan otomatis.
        </p>
        {resourceNowError && (
          <p role="alert" className="text-sm text-amber-300 mt-3">
            Listing baru tidak dimulai: {resourceNowError}. Hasil di bawah berasal dari percobaan sebelumnya.
          </p>
        )}
        {resourceNowRun.status !== "idle" && (
          <div className="text-xs text-slate-400 mt-2" aria-live="polite">
            <p>Hasil listing terakhir{resourceNowRun.created_at ? ` (${new Date(resourceNowRun.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", hour12: false })} WIB)` : ""}: {resourceNowRun.status === "needs_review" ? "perlu diperiksa" : resourceNowRun.status}{resourceNowRun.current_account_id ? ` · ${labels[resourceNowRun.current_account_id] || "akun diproses"}` : ""}</p>
            {Object.entries(resourceNowRun.accounts || {}).map(([accountId, result]) => (
              <p key={accountId}>
                {labels[accountId] || "Akun"}: {result.listed?.length || 0} ikan terpasang
                {result.stopped_reason ? ` · berhenti: ${result.stopped_reason}` : ""}
              </p>
            ))}
            {resourceNowRun.error && <p>{resourceNowRun.error}</p>}
          </div>
        )}
        {resourceSettings.next_run_at && (
          <p className="text-xs text-slate-400 mt-2">
            Jadwal berikutnya: {new Date(resourceSettings.next_run_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", hour12: false })} WIB
          </p>
        )}
        {resourceRun.status !== "idle" && (
          <p className="text-xs text-slate-400 mt-2">
            Listing harian terakhir: {resourceRun.date} · {resourceRun.status}
          </p>
        )}
      </section>

      <section id="coin-accounts" className="scroll-mt-24">
        <h2 className="font-heading text-xl font-bold mb-2">Peran akun</h2>
        <p className="text-sm text-slate-400 mb-5">Tentukan akun utama, collector, dan source sebelum mengumpulkan koin.</p>
        {accountsError && (
          <p role="alert" className="mb-4 text-sm text-amber-300">
            Daftar akun gagal dimuat. <button type="button" onClick={() => load()} className="underline">Coba lagi</button>
          </p>
        )}
        {configError && (
          <p role="alert" className="mb-4 text-sm text-amber-300">
            Pengaturan peran belum dimuat. <button type="button" onClick={() => load()} className="underline">Coba lagi</button>
          </p>
        )}
        <div className="hidden md:grid grid-cols-[minmax(150px,1.4fr)_minmax(115px,.8fr)_minmax(120px,1fr)_70px] gap-3 px-3 pb-2 text-xs uppercase text-slate-500">
          <span>Akun</span><span>Peran</span><span>Collector</span><span>VIP</span>
        </div>
        {accounts.map((account) => {
          const role = roleOf(account.id);
          return (
            <div key={account.id} className="grid grid-cols-2 md:grid-cols-[minmax(150px,1.4fr)_minmax(115px,.8fr)_minmax(120px,1fr)_70px] gap-3 items-center border-t border-white/[0.06] px-2 sm:px-3 py-4">
              <div className="col-span-2 md:col-span-1 min-w-0">
                <div className="truncate font-medium">{account.label}</div>
                <div className={`text-xs ${account.connected ? "text-emerald-400" : "text-slate-600"}`}>
                  {account.connected ? "Terhubung" : "Tidak terhubung"}
                </div>
                {account.profile?.status === "syncing" && (
                  <div className="text-xs text-yellow-400">Membaca Mini App...</div>
                )}
                {account.profile?.status === "ready" && (
                  <div className="text-xs text-slate-500">Data Mini App diperbarui</div>
                )}
                {account.profile?.status === "error" && (
                  <div className="text-xs text-amber-400">Mini App belum terbaca</div>
                )}
              </div>
              <div>
                <label htmlFor={`role-${account.id}`} className="md:hidden block mb-1 text-xs text-slate-400">Peran</label>
                <select id={`role-${account.id}`} value={role} onChange={(event) => setRole(account.id, event.target.value)} disabled={configError} className="w-full h-11 rounded-xl border border-white/10 bg-[#0b1420] px-2 text-sm">
                  <option value="ignored">Abaikan</option>
                  <option value="primary">Utama</option>
                  <option value="collector">Collector</option>
                  <option value="source">Source</option>
                </select>
              </div>
              <div>
                <label htmlFor={`collector-${account.id}`} className="md:hidden block mb-1 text-xs text-slate-400">Collector</label>
                {role === "source" ? (
                  <select id={`collector-${account.id}`} value={config.source_to_collector[account.id] || ""} onChange={(event) => setCollector(account.id, event.target.value)} disabled={configError} className="w-full h-11 rounded-xl border border-white/10 bg-[#0b1420] px-2 text-sm">
                    {config.collector_account_ids.map((id) => <option key={id} value={id}>{labels[id]}</option>)}
                  </select>
                ) : <span className="hidden md:block text-slate-700">-</span>}
              </div>
              <div className="col-span-2 md:col-span-1 flex items-center gap-3">
                <span className="md:hidden text-xs text-slate-400">VIP</span>
                <Switch checked={config.vip_account_ids.includes(account.id)} onCheckedChange={(checked) => toggleVip(account.id, checked)} disabled={configError} aria-label={`VIP ${account.label}`} />
              </div>
            </div>
          );
        })}
        <div className="pt-4">
          <Button onClick={save} disabled={saving || configError} variant="outline" className="border-white/10">
            <FloppyDisk size={17} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </div>
      </section>

      <section id="coin-collection" className="scroll-mt-24">
        <h2 className="font-heading text-xl font-bold mb-2">Batch pengumpulan koin</h2>
        <p className="text-sm text-slate-500 mb-4">
          Baca saldo dan slot akun secara live, lalu jalankan resource → collector → utama. Alur ini melewati listing resource harian.
        </p>
        <Button
          onClick={previewCollection} disabled={collectionPlanning || Boolean(execution?.run_id && ["queued", "running", "needs_review"].includes(execution.status))}
          variant="outline" className="border-white/10"
        >
          {collectionPlanning ? "Membaca saldo..." : "Siapkan Batch Live"}
        </Button>
        {collectionPlan && (
          <div className="mt-4 rounded-lg border border-yellow-400/20 bg-yellow-400/[0.04] p-4 text-sm">
            <p>
              <strong>{collectionPlan.transactions.length} transaksi</strong> · pajak perkiraan {coins(collectionPlan.tax_total)} · masuk ke utama {coins(collectionPlan.summary?.delivered_to_primary)}
            </p>
            {(collectionPlan.transactions || []).map((item) => (
              <div key={item.sequence} className="flex flex-wrap justify-between gap-2 border-t border-white/[0.06] py-2 mt-2">
                <span>{labels[item.buyer_account_id]} → {labels[item.seller_account_id]}</span>
                <strong>{coins(item.price)}</strong>
              </div>
            ))}
            {(collectionPlan.pending_accounts || []).length > 0 && (
              <p className="text-amber-300 mt-2">
                Sebagian saldo menunggu slot atau ikan berikutnya: {coins(collectionPlan.summary?.remaining_outside_primary)}.
              </p>
            )}
            <Button
              onClick={startCollection} disabled={collectionStarting}
              className="mt-4 bg-red-500 text-white hover:bg-red-400"
              data-testid="coin-transfer-execute"
            >
              {collectionStarting ? "Memulai..." : "Jalankan Batch Live"}
            </Button>
          </div>
        )}
      </section>

      <section id="coin-simulation" className="scroll-mt-24">
        <h2 className="font-heading text-lg font-bold mb-1">Simulasi manual</h2>
        <p className="text-xs text-slate-500 mb-4">Angka di bawah dapat diedit untuk perencanaan. Batch yang dijalankan selalu memakai saldo dan slot akun yang dibaca langsung.</p>
        <div className="hidden lg:grid grid-cols-[minmax(150px,1.4fr)_1fr_100px_100px] gap-3 px-3 pb-2 text-xs uppercase text-slate-500">
          <span>Akun</span><span>Saldo</span><span>Listing terpakai</span><span>Normal-Legend</span>
        </div>
        {accounts.map((account) => (
          <div key={account.id} className="grid grid-cols-2 lg:grid-cols-[minmax(150px,1.4fr)_1fr_100px_100px] gap-3 items-center border-t border-white/[0.06] px-2 sm:px-3 py-4">
            <span className="col-span-2 lg:col-span-1 font-semibold truncate">{account.label}</span>
            <div className="col-span-2 sm:col-span-1 lg:col-span-1">
              <label htmlFor={`balance-${account.id}`} className="lg:hidden block mb-1 text-xs text-slate-400">Saldo</label>
              <Input id={`balance-${account.id}`} type="number" min="0" value={snapshots[account.id]?.balance || 0} onChange={(event) => updateSnapshot(account.id, "balance", event.target.value)} aria-label={`Saldo ${account.label}`} className="bg-[#0b1420] border-white/10" />
            </div>
            <div>
              <label htmlFor={`listings-${account.id}`} className="lg:hidden block mb-1 text-xs text-slate-400">Listing</label>
              <Input id={`listings-${account.id}`} type="number" min="0" max="3" value={snapshots[account.id]?.used_listings || 0} onChange={(event) => updateSnapshot(account.id, "used_listings", event.target.value)} aria-label={`Listing terpakai ${account.label}`} className="bg-[#0b1420] border-white/10" />
            </div>
            <div>
              <label htmlFor={`items-${account.id}`} className="lg:hidden block mb-1 text-xs text-slate-400">Normal-Legend</label>
              <Input id={`items-${account.id}`} type="number" min="0" value={snapshots[account.id]?.available_items || 0} onChange={(event) => updateSnapshot(account.id, "available_items", event.target.value)} aria-label={`Item ${account.label}`} className="bg-[#0b1420] border-white/10" />
            </div>
          </div>
        ))}
        <div className="pt-4">
          <Button onClick={makePlan} disabled={planning} className="bg-yellow-500 text-black hover:bg-yellow-400">
            <Play size={17} weight="fill" className="mr-2" /> {planning ? "Menghitung..." : "Buat Simulasi"}
          </Button>
        </div>
      </section>

      {plan && (
        <section className="border-t border-white/10 pt-5" data-testid="coin-transfer-plan">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm mb-5">
            <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
              <div className="text-xs text-slate-500">Transaksi hari ini</div>
              <strong>{plan.summary?.transaction_count ?? plan.transactions.length}</strong>
            </div>
            <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
              <div className="text-xs text-slate-500">Perkiraan masuk ke Utama</div>
              <strong>{coins(plan.summary?.delivered_to_primary)}</strong>
            </div>
            <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
              <div className="text-xs text-slate-500">Sisa akun yang ikut</div>
              <strong>{coins(plan.summary?.remaining_outside_primary)}</strong>
            </div>
            <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
              <div className="text-xs text-slate-500">Status simulasi</div>
              <strong className={plan.summary?.complete_for_today ? "text-emerald-400" : "text-amber-300"}>
                {plan.summary?.complete_for_today ? "Tuntas hari ini" : "Perlu batch berikutnya"}
              </strong>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 text-sm mb-4">
            <span>Pajak: <strong>{coins(plan.tax_total)}</strong></span>
            <span>Reset: <strong>{new Date(plan.rules.next_reset_at).toLocaleString("id-ID", { hour12: false })}</strong></span>
          </div>
          {plan.transactions.map((item) => (
            <div key={item.sequence} className="grid md:grid-cols-[50px_1fr_30px_1fr_1fr] gap-2 items-center border-t border-white/[0.06] py-3 text-sm">
              <strong>#{item.sequence}</strong>
              <span>{labels[item.buyer_account_id]}</span>
              <span className="text-slate-600">→</span>
              <span>{labels[item.seller_account_id]}</span>
              <span className="font-mono text-yellow-300">{coins(item.price)}</span>
            </div>
          ))}
          {plan.transactions.length === 0 && <p className="text-slate-500 py-4">Tidak ada transaksi.</p>}
          {(plan.pending_accounts || []).length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-400/20 bg-amber-400/[0.04] p-3">
              <p className="text-amber-200 text-sm font-medium">Saldo yang menunggu slot atau item berikutnya:</p>
              {(plan.pending_accounts || []).map((item) => (
                <p key={item.account_id} className="text-amber-300 text-sm mt-1">
                  {labels[item.account_id] || "Akun"}: {coins(item.balance)}
                </p>
              ))}
            </div>
          )}
          {(plan.warnings || [])
            .filter((warning) => !(plan.pending_accounts || []).length || !warning.startsWith("Masih ada "))
            .map((warning) => <p key={warning} className="text-amber-300 text-sm mt-2">{warning}</p>)}
          {(plan.summary?.ignored_account_count || 0) > 0 && (
            <p className="text-slate-500 text-xs mt-3">
              {plan.summary.ignored_account_count} akun berstatus Abaikan tidak dihitung sebagai saldo tersisa.
            </p>
          )}
          {plan.transactions.length > 0 && (
            <div className="mt-5">
              <Button
                onClick={previewCollection}
                disabled={collectionPlanning || Boolean(execution?.run_id && ["queued", "running", "needs_review"].includes(execution.status))}
                className="bg-yellow-500 text-black hover:bg-yellow-400"
                data-testid="coin-transfer-live-preview"
              >
                <Play size={17} weight="fill" className="mr-2" />
                {collectionPlanning ? "Membaca saldo..." : "Siapkan Batch Live"}
              </Button>
              <p className="text-xs text-slate-500 mt-2">Anda akan meninjau transaksi live sebelum menjalankannya.</p>
            </div>
          )}
        </section>
      )}

      {execution && (
        <section className="border-t border-white/10 pt-5" data-testid="coin-transfer-execution">
          <h2 className="font-heading text-xl font-bold mb-3">Status Batch</h2>
          <p className="text-sm mb-3">
            Status: <strong className="text-yellow-300">{execution.status}</strong>
          </p>
          {(execution.transactions || []).map((item) => (
            <div key={item.sequence} className="border-t border-white/[0.06] py-3 text-sm">
              <div className="flex flex-wrap justify-between gap-2">
                <span>#{item.sequence} {labels[item.buyer_account_id]} → {labels[item.seller_account_id]}</span>
                <strong>{item.status}</strong>
              </div>
              {item.error && <p className="text-amber-300 mt-1">{item.error}</p>}
            </div>
          ))}
          {execution.status === "needs_review" && (
            <div className="mt-3 space-y-3">
              <p className="text-red-300 text-sm">
                Hasil transaksi belum pasti. Periksa listing dan saldo kedua akun sebelum menutup batch.
              </p>
              <Button onClick={closeExecution} disabled={closingExecution} variant="outline">
                {closingExecution ? "Menutup..." : "Tutup Batch Setelah Diperiksa"}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
